import {
  ProductV2DataType,
  ProductV2Section,
  ProductV2StoryCard,
  productV2Data,
} from "@/shared/data/product.v2.data";
import { ProductPageSlugEnum } from "@/shared/types/common.types";
import {
  deepSwapImageUrls,
  ImageSwapMap,
} from "@/shared/data/internal/us-image-swap";
import { getCDNURL } from "@/shared/utils/commons";

/**
 * product.v2.data.us — US locale overrides for V2 product pages.
 *
 * Image paths and the stories-section creator cards differ from India.
 * Image swaps below list the India substring to match → US substring
 * to swap in. Paths are relative to `graphy-web/V4/images/`.
 *
 * Stories section is replaced wholesale with US creators; each product
 * page has its own US testimonial folder on the CDN (folder names are
 * inconsistent — some use "Testimonial images" and others the typo
 * "Testimnial images" — mirrored exactly here).
 */

const productSwaps: Partial<Record<ProductPageSlugEnum, ImageSwapMap>> = {
  [ProductPageSlugEnum.courses]: {
    "Courses Page/Sell Section/Track Sales, Revenue & Engagement.png":
      "Courses Page/Courses US Images/Sell Section/Track Sales, Revenue & Engagement.png",
  },
  [ProductPageSlugEnum.webinar]: {
    "Webinar Page/Sell Section/Choose Your Webinar Format.png":
      "Webinar Page/Webinar US Images/Create Carousel Section/Choose Your Webinar Format.png",
    "Webinar Page/Sell Section/Launch Your Webinar In Minutes.png":
      "Webinar Page/Webinar US Images/Create Carousel Section/Launch Your Webinar In Minutes.png",
    "Webinar Page/Sell Section 02/Track Sales, Revenue & Engagement.png":
      "Webinar Page/Webinar US Images/Sell Section/Track Sales, Revenue & Engagement.png",
  },
  [ProductPageSlugEnum.membership]: {
    "Membership Page/Create Section/Build Engaging Community Spaces.png":
      "Membership Page/Membership US Images/Create Section/Build Engaging Community Spaces.png",
    "Membership Page/Create Section/Define Your Membership Model.png":
      "Membership Page/Membership US Images/Create Section/Define Your Membership Model.png",
    "Membership Page/Create Section/Launch Your Membership In Minutes.png":
      "Membership Page/Membership US Images/Create Section/Launch Your Membership In Minutes.png",
    // US set renames "Track Sales, Revenue & Engagement" → "Track Sales , Revenue image".
    "Membership Page/Sell Section/Track Sales, Revenue & Engagement.png":
      "Membership Page/Membership US Images/Sell Section/Track Sales , Revenue image.png",
  },
  [ProductPageSlugEnum.digitalProducts]: {
    "Digital Products Page/Create Section/Design A Seamless Buyer Experience.png":
      "Digital Products Page/Digital Products US Images/Create Section/Design A Seamless Buyer Experience.png",
    "Digital Products Page/Create Section/Launch Your Digital Store In Minutes.png":
      "Digital Products Page/Digital Products US Images/Create Section/Launch Your Digital Store In Minutes.png",
    "Digital Products Page/Sell Section/Track Sales, Revenue & Engagement.png":
      "Digital Products Page/Digital Products US Images/Sell Section/Track Sales, Revenue & Engagement.png",
  },
  [ProductPageSlugEnum.telegramCommunity]: {
    "Community Page/Create Section/Create Content & Conversation Spaces.png":
      "Community Page/Community US Images/Create Section/Create Content & Conversation Spaces.png",
    "Community Page/Create Section/Manage Your Community With Ease.png":
      "Community Page/Community US Images/Create Section/Manage Your Community With Ease.png",
    "Community Page/Sell Section/Track Sales, Revenue & Engagement.png":
      "Community Page/Community US Images/Sell Section/Track Sales, Revenue & Engagement.png",
  },
  [ProductPageSlugEnum.aiavatar]: {
    "AI Avatar Page/Create Section/Launch Your AI Avatar In Minutes.png":
      "AI Avatar Page/AI Avatar US Images/Create Section/Launch Your AI Avatar In Minutes.png",
    "AI Avatar Page/Create Section/Set Rules, Permissions & Boundaries for Your Avatar.png":
      "AI Avatar Page/AI Avatar US Images/Create Section/Set Rules, Permissions & Boundaries for Your Avatar.png",
    "AI Avatar Page/Sell Section/Track Sales, Revenue & Engagement.png":
      "AI Avatar Page/AI Avatar US Images/Sell Section/Track Sales, Revenue & Engagement.png",
    "AI Avatar Page/Sell Section/Accept Payments Seamlessly.png":
      "AI Avatar Page/AI Avatar US Images/Sell Section/Accept Payments Seamlessly.png",
  },
  // India uses the generic Home-page coaching visual for the hero; US gets
  // its own dedicated hero artwork. All other coaching images are shared.
  [ProductPageSlugEnum.coaching]: {
    "Home Page/Features Section/Coaching.png":
      "Product Pages/Sell Coaching/Create/Hero US.png",
  },
};

// Currency swap applied to every slug so US hero stats read "$1.5 Billion+"
// instead of the India-facing "₹1,500 Crore+".
const currencySwap: ImageSwapMap = {
  "₹1,500 Crore+": "$1.5 Billion+",
};

// Per-slug CDN folder for US creator avatars. Some folders are spelled
// "Testimnial" on S3; others are "Testimonial" — mirrored verbatim here.
const US_STORY_AVATAR_FOLDER: Partial<Record<ProductPageSlugEnum, string>> = {
  [ProductPageSlugEnum.courses]:
    "Courses Page/Courses US Images/Testimonial images",
  [ProductPageSlugEnum.webinar]:
    "Webinar Page/Webinar US Images/Testimnial images",
  [ProductPageSlugEnum.membership]:
    "Membership Page/Membership US Images/Testimnial images",
  [ProductPageSlugEnum.digitalProducts]:
    "Digital Products Page/Digital Products US Images/Testimnial images",
  [ProductPageSlugEnum.telegramCommunity]:
    "Community Page/Community US Images/Testimonial images",
  [ProductPageSlugEnum.aiavatar]:
    "AI Avatar Page/AI Avatar US Images/Testimonial images",
};

type UsStoryTemplate = Omit<ProductV2StoryCard, "avatar"> & {
  avatarFile: string;
};

const US_STORY_TEMPLATES: UsStoryTemplate[] = [
  {
    title: "Responsive Team",
    quote:
      "I can save a pet's life, I don't know how to build tech platforms. That's why I appreciate the Graphy team — they were accessible, responsive, and helped troubleshoot every step.",
    name: "Arden Moore",
    role: "Coach",
    avatarFile: "Arden Moore.png",
  },
  {
    title: "Everything In One Place",
    quote:
      "With AI Avatar, I realized I could literally build myself into their phones. It's my way of being there — even when I can't. And I'm not a tech person, I'm a teacher. Graphy lets me stay that way.",
    name: "Amanda Whittakar",
    role: "Founder | Elevated by Amanda",
    avatarFile: "Amanda Whittakar.png",
  },
  {
    title: "Graphy Helps You Grow",
    quote:
      "I was spending money, but the platform never checked if I needed help. I couldn't figure out the tech. My work stayed locked in my laptop. When I joined Graphy, I was paired with a real human coach. For the first time, tech wasn't the barrier… it was the bridge.",
    name: "Jay Daniels",
    role: "Spirituality Coach",
    avatarFile: "Jay Daniels.png",
  },
  {
    title: "Brilliant Platform",
    quote:
      "From sales right through to design and follow-up Graphy were brilliant. It was easy to get connected and the service was always excellent. I am so happy I chose Graphy that I have placed a second business website with them as well.",
    name: "Bradley Thomson",
    role: "Founder, Foundation Co.",
    avatarFile: "Bradley Thomson.png",
  },
  {
    title: "Great Support",
    quote:
      "Overall, I'm obsessed with how Graphy puts people first! As someone who runs both webinars and a monthly bootcamp, I'm continually blown away by how their team goes above and beyond with support for every creator.",
    name: "Kimberly Clark",
    role: "Founder, Flawsome Media",
    avatarFile: "Kimberly Clark.png",
  },
  {
    title: "My Website Looks Beautiful",
    quote:
      "My website looks like it was professionally designed and the courses are laid out beautifully and easy for users to interact with and understand. The Graphy team has been super helpful, responsive and patient while guiding me through this process and helping me get my site and business launched. I'm so grateful this exists.",
    name: "Brooke Harris",
    role: "CEO | Goodmylk",
    avatarFile: "Brooke Harris.png",
  },
];

// All 6 US creator avatars are the same headshots regardless of which
// product page renders them — historically uploaded to a per-product
// "Testimonial images" folder under each "<Page> US Images/" tree. With
// V4 they collapse onto a single shared folder; the per-slug folder map
// (US_STORY_AVATAR_FOLDER) above now only determines whether avatars are
// shown for that surface, not where they live on S3.
const US_TESTIMONIAL_BASE = `${getCDNURL()}graphy-web/V4/images/Testimonial Images/US`;

/**
 * Build the 6 US creator story cards. The `folder` argument is now a
 * truthy gate — pass any string to render avatars, undefined to omit
 * them. Avatar URLs always resolve to the shared US testimonial folder.
 */
export const buildUsStoriesFromFolder = (
  folder?: string
): ProductV2StoryCard[] =>
  US_STORY_TEMPLATES.map((tpl) => ({
    title: tpl.title,
    quote: tpl.quote,
    name: tpl.name,
    role: tpl.role,
    avatar: folder ? `${US_TESTIMONIAL_BASE}/${tpl.avatarFile}` : undefined,
  }));

const buildUsStories = (slug: ProductPageSlugEnum): ProductV2StoryCard[] =>
  buildUsStoriesFromFolder(US_STORY_AVATAR_FOLDER[slug]);

export const usStoriesForSlug = buildUsStories;

const applyUsStories = (
  slug: ProductPageSlugEnum,
  data: ProductV2DataType
): ProductV2DataType => {
  const folder = US_STORY_AVATAR_FOLDER[slug];
  if (!folder) return data;
  const usStories = buildUsStories(slug);
  const nextSections: ProductV2Section[] = data.sections.map((section) =>
    section.kind === "stories"
      ? { ...section, data: { ...section.data, stories: usStories } }
      : section
  );
  return { ...data, sections: nextSections };
};

export const productV2DataUs: Partial<
  Record<ProductPageSlugEnum, ProductV2DataType>
> = Object.fromEntries(
  Object.entries(productV2Data).map(([slug, data]) => {
    if (!data) return [slug, data];
    const slugEnum = slug as ProductPageSlugEnum;
    const imageSwaps = productSwaps[slugEnum];
    let next = data;
    if (imageSwaps) next = deepSwapImageUrls(next, imageSwaps);
    next = deepSwapImageUrls(next, currencySwap);
    next = applyUsStories(slugEnum, next);
    return [slug, next];
  })
);
